const BASE = process.env.REACT_APP_API_URL || '/api';

// Token storage
export function getToken() {
  return localStorage.getItem('kitchen_token');
}
export function setToken(token) {
  localStorage.setItem('kitchen_token', token);
}
export function clearToken() {
  localStorage.removeItem('kitchen_token');
}

// Authenticated fetch wrapper
async function apiFetch(path, options = {}) {
  const token = getToken();
  const headers = {
    'Content-Type': 'application/json',
    ...(token ? { Authorization: `Bearer ${token}` } : {}),
    ...options.headers,
  };
  const r = await fetch(`${BASE}${path}`, { ...options, headers });
  if (r.status === 401) {
    clearToken();
    window.dispatchEvent(new Event('auth:logout'));
    throw new Error('Session expired');
  }
  if (!r.ok) throw new Error(`Request failed: ${r.status}`);
  return r.json();
}

// --- Auth ---
export async function login(password, kiosk = false) {
  const r = await fetch(`${BASE}/auth/login`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ password, kiosk }),
  });
  if (r.status === 401) throw new Error('Incorrect password');
  if (!r.ok) throw new Error('Login failed');
  const data = await r.json();
  setToken(data.token);
  return data;
}

// --- Grocery ---
export async function fetchGrocery() {
  return apiFetch('/grocery');
}
export async function addGroceryItem(name) {
  return apiFetch('/grocery', {
    method: 'POST',
    body: JSON.stringify({ name }),
  });
}
export async function toggleGroceryItem(id, checked) {
  return apiFetch(`/grocery/${id}`, {
    method: 'PATCH',
    body: JSON.stringify({ checked }),
  });
}
export async function deleteGroceryItem(id) {
  return apiFetch(`/grocery/${id}`, { method: 'DELETE' });
}
export async function clearCheckedItems() {
  return apiFetch('/grocery/checked/all', { method: 'DELETE' });
}

// --- Meals ---
export async function fetchMeals(start, end) {
  const params = start && end ? `?start=${start}&end=${end}` : '';
  return apiFetch(`/meals${params}`);
}
export async function setMeal(date, meal_name) {
  return apiFetch(`/meals/${date}`, {
    method: 'PUT',
    body: JSON.stringify({ meal_name }),
  });
}
export async function deleteMeal(date) {
  return apiFetch(`/meals/${date}`, { method: 'DELETE' });
}

// --- Calendar ---
export async function fetchCalendar(days = 7) {
  return apiFetch(`/calendar?days=${days}`);
}

// --- Saved Grocery ---
export async function fetchSavedGrocery() {
  return apiFetch('/saved-grocery');
}
export async function addSavedGroceryItem(name) {
  return apiFetch('/saved-grocery', {
    method: 'POST',
    body: JSON.stringify({ name }),
  });
}
export async function deleteSavedGroceryItem(id) {
  return apiFetch(`/saved-grocery/${id}`, { method: 'DELETE' });
}

// --- Saved Meals ---
export async function fetchSavedMeals() {
  return apiFetch('/saved-meals');
}
export async function addSavedMeal(meal_name) {
  return apiFetch('/saved-meals', {
    method: 'POST',
    body: JSON.stringify({ meal_name }),
  });
}
export async function deleteSavedMeal(id) {
  return apiFetch(`/saved-meals/${id}`, { method: 'DELETE' });
}

// --- Pet Tracker ---
export async function fetchPetsToday() {
  return apiFetch('/pets/today');
}
export async function feedPet(petId) {
  return apiFetch(`/pets/${petId}/feed`, { method: 'POST' });
}
export async function resetFeed(petId) {
  return apiFetch(`/pets/${petId}/feed/reset`, { method: 'POST' });
}
export async function toggleMed(medId) {
  return apiFetch(`/meds/${medId}/toggle`, { method: 'POST' });
}
export async function incrementMed(medId) {
  return apiFetch(`/meds/${medId}/increment`, { method: 'POST' });
}
export async function resetMed(medId) {
  return apiFetch(`/meds/${medId}/reset`, { method: 'POST' });
}
export async function fetchWalksToday() {
  return apiFetch('/walks/today');
}
export async function toggleWalk(walkType) {
  return apiFetch(`/walks/${walkType}/toggle`, { method: 'POST' });
}

// --- Pet Management ---
export async function fetchAllPets() {
  return apiFetch('/pets');
}
export async function createPet(name, target_scoops, is_dog) {
  return apiFetch('/pets', {
    method: 'POST',
    body: JSON.stringify({ name, target_scoops, is_dog }),
  });
}
export async function updatePet(id, fields) {
  return apiFetch(`/pets/${id}`, {
    method: 'PATCH',
    body: JSON.stringify(fields),
  });
}
export async function deletePet(id) {
  return apiFetch(`/pets/${id}`, { method: 'DELETE' });
}
export async function addPetMed(petId, med_name, quantity) {
  return apiFetch(`/pets/${petId}/meds`, {
    method: 'POST',
    body: JSON.stringify({ med_name, quantity }),
  });
}
export async function updatePetMed(petId, medId, med_name, quantity) {
  return apiFetch(`/pets/${petId}/meds/${medId}`, {
    method: 'PATCH',
    body: JSON.stringify({ med_name, quantity }),
  });
}
export async function deletePetMed(petId, medId) {
  return apiFetch(`/pets/${petId}/meds/${medId}`, { method: 'DELETE' });
}

// --- Roster ---
export async function fetchRoster() {
  return apiFetch('/roster');
}
export async function addRosterMember(name, color, is_default) {
  return apiFetch('/roster', {
    method: 'POST',
    body: JSON.stringify({ name, color, is_default }),
  });
}
export async function updateRosterMember(id, fields) {
  return apiFetch(`/roster/${id}`, {
    method: 'PATCH',
    body: JSON.stringify(fields),
  });
}
export async function deleteRosterMember(id) {
  return apiFetch(`/roster/${id}`, { method: 'DELETE' });
}
export async function setMealCook(date, cook_id) {
  return apiFetch(`/meals/${date}/cook`, {
    method: 'PATCH',
    body: JSON.stringify({ cook_id }),
  });
}

// --- Reports ---
export async function fetchGroceryReport() {
  return apiFetch('/reports/grocery');
}
export async function fetchMealsReport() {
  return apiFetch('/reports/meals');
}
export async function fetchPetsReport() {
  return apiFetch('/reports/pets');
}
export async function fetchChoresReport() {
  return apiFetch('/reports/chores');
}

// --- Chores ---
export async function fetchChores() {
  return apiFetch('/chores');
}
export async function createChore(name, room, frequency, assigned_to) {
  return apiFetch('/chores', {
    method: 'POST',
    body: JSON.stringify({ name, room, frequency, assigned_to }),
  });
}
export async function updateChore(id, fields) {
  return apiFetch(`/chores/${id}`, {
    method: 'PATCH',
    body: JSON.stringify(fields),
  });
}
export async function deleteChore(id) {
  return apiFetch(`/chores/${id}`, { method: 'DELETE' });
}
export async function completeChore(id, completed_by) {
  return apiFetch(`/chores/${id}/complete`, {
    method: 'POST',
    body: JSON.stringify({ completed_by }),
  });
}
export async function resetChore(id) {
  return apiFetch(`/chores/${id}/complete`, { method: 'DELETE' });
}
